import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Select, Space, Table, Tag, message } from "antd";
import moment from "moment";
import { Body } from "../../../components/main-body/body";
import ApiAdminBackfill from "../../../api/admin-backfill";
import ApiPlanManagement from "../../../api/plan-management";
import ApiSubjectsManagement from "../../../api/subjects-manager";
import "./planning-audit.css";

const STATUS_COLORS = {
  completed: "green",
  failed: "red",
  started: "gold",
  missing: "orange",
};

const PlanningAudit = () => {
  const [weekStartDate, setWeekStartDate] = useState(moment().startOf("week"));
  const [planId, setPlanId] = useState();
  const [subjectId, setSubjectId] = useState();
  const [audit, setAudit] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subjects, setSubjects] = useState([]);

  // Plans + subjects feed the filter dropdowns and the in-table label
  // resolution for the Plan column (we render title instead of raw uuid).
  useEffect(() => {
    let cancelled = false;
    ApiPlanManagement.getPlanList()
      .then(({ data }) => {
        if (!cancelled) setPlans(data?.plans || []);
      })
      .catch(() => {});
    ApiSubjectsManagement.getSubjectsSimple()
      .then(({ data }) => {
        if (!cancelled) setSubjects(data?.subjects || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const planById = useMemo(() => {
    const map = new Map();
    plans.forEach((p) => map.set(p.id, p));
    return map;
  }, [plans]);

  const planOptions = useMemo(
    () => plans.map((p) => ({ value: p.id, label: p.title || p.id })),
    [plans]
  );
  const subjectOptions = useMemo(
    () => subjects.map((s) => ({
      value: s.id,
      label: s.displayId || s.id,
    })),
    [subjects]
  );

  const query = useMemo(() => ({
    weekStartDate: weekStartDate ? weekStartDate.format("YYYY-MM-DD") : undefined,
    planId: planId || undefined,
    subjectId: subjectId || undefined,
  }), [planId, subjectId, weekStartDate]);

  const loadAudit = useCallback(async () => {
    if (!query.weekStartDate) {
      message.error("Week start date is required");
      return;
    }

    setLoading(true);
    try {
      const response = await ApiAdminBackfill.getPlanningAudit(query);
      setAudit(response.data);
      setSelectedRowKeys([]);
    } catch (error) {
      console.error("Planning audit failed:", error);
      message.error(error.response?.data?.error || "Failed to load planning audit");
    } finally {
      setLoading(false);
    }
  }, [query]);

  const recover = async (selectedOnly) => {
    if (!query.weekStartDate) {
      message.error("Week start date is required");
      return;
    }

    const subjectIds = selectedOnly ? selectedRowKeys : undefined;
    if (selectedOnly && selectedRowKeys.length === 0) {
      message.error("Select at least one subject");
      return;
    }

    setRecovering(true);
    try {
      const response = await ApiAdminBackfill.recoverPlanning({
        ...query,
        subjectIds,
      });
      message.success(`Recovered ${response.data.recovered || 0} subject(s)`);
      await loadAudit();
    } catch (error) {
      console.error("Planning recovery failed:", error);
      message.error(error.response?.data?.error || "Failed to recover planning");
    } finally {
      setRecovering(false);
    }
  };

  const rows = audit?.subjects || [];
  const summary = [
    ["Expected", audit?.expectedSubjects || 0],
    ["Completed", audit?.completed || 0],
    ["Missing", audit?.missing || 0],
    ["Failed", audit?.failed || 0],
    ["Started", audit?.started || 0],
  ];

  const columns = [
    {
      title: "Status",
      dataIndex: "ledgerStatus",
      width: 120,
      render: (status) => <Tag color={STATUS_COLORS[status] || "default"}>{status}</Tag>,
    },
    {
      title: "Subject",
      dataIndex: "displayId",
      width: 180,
      render: (value, row) => value || row.email || row.subjectId,
    },
    {
      title: "Email",
      dataIndex: "email",
      width: 220,
      render: (value) => value || "-",
    },
    {
      title: "Timezone",
      dataIndex: "timeZone",
      width: 170,
      render: (value) => <span className="planning-audit-code">{value || "UTC"}</span>,
    },
    {
      title: "Week index",
      dataIndex: "weekIndex",
      width: 110,
      render: (value) => value ?? "-",
    },
    {
      title: "Plan",
      dataIndex: "planId",
      width: 240,
      render: (value) => {
        if (!value) return "-";
        const title = planById.get(value)?.title;
        // Fall back to the raw uuid (in a code chip) only if we genuinely
        // can't resolve the plan — happens if the plan was deleted or lives
        // in a project the current user can't see.
        return title ? title : <span className="planning-audit-code">{value}</span>;
      },
    },
    {
      title: "Error",
      dataIndex: "error",
      ellipsis: true,
      render: (value) => value || "-",
    },
  ];

  return (
    <Body header={{ title: "Planning audit" }} scroll>
      <div className="planning-audit-page">
        <div className="planning-audit-filters">
          <DatePicker
            value={weekStartDate}
            onChange={setWeekStartDate}
            allowClear={false}
            format="YYYY-MM-DD"
          />
          <Select
            value={planId}
            onChange={setPlanId}
            options={planOptions}
            placeholder="Plan"
            showSearch
            allowClear
            optionFilterProp="label"
            style={{ minWidth: 220 }}
          />
          <Select
            value={subjectId}
            onChange={setSubjectId}
            options={subjectOptions}
            placeholder="Subject"
            showSearch
            allowClear
            optionFilterProp="label"
            style={{ minWidth: 220 }}
          />
          <Button onClick={loadAudit} loading={loading}>
            Check
          </Button>
          <Space>
            <Button
              onClick={() => recover(true)}
              disabled={selectedRowKeys.length === 0}
              loading={recovering}
            >
              Recover selected
            </Button>
            <Button
              type="primary"
              onClick={() => recover(false)}
              disabled={rows.length === 0}
              loading={recovering}
            >
              Recover all
            </Button>
          </Space>
        </div>

        <div className="planning-audit-summary">
          {summary.map(([label, value]) => (
            <div className="planning-audit-summary-item" key={label}>
              <div className="planning-audit-summary-label">{label}</div>
              <div className="planning-audit-summary-value">{value}</div>
            </div>
          ))}
        </div>

        <Table
          rowKey="subjectId"
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          scroll={{ x: 1300 }}
        />
      </div>
    </Body>
  );
};

export default PlanningAudit;
